import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { Check, Copy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/Button'
import { useI18n } from '@/i18n/i18n'
import { api } from '@/lib/api'

// Settings: letting outside AI apps (MCP clients such as Claude Code) work on
// your boards. Where they connect, how to add moot to each, and the apps you
// have let in, each of which can be cut off.

type Client = 'claudeCode' | 'claude' | 'other'

function CopyField({ value, label }: { value: string; label: string }) {
  const { t } = useI18n()
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-stretch gap-2">
      <input
        readOnly
        aria-label={label}
        value={value}
        onFocus={(e) => {
          e.currentTarget.select()
        }}
        className="h-9 min-w-0 flex-1 rounded-md border border-line bg-bg px-3 font-mono text-[12.5px] outline-none focus:border-(--focus)"
      />
      <Button
        size="sm"
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true)
            setTimeout(() => {
              setCopied(false)
            }, 1500)
          })
        }}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? t('apps.copied') : t('apps.copy')}
      </Button>
    </div>
  )
}

export function ConnectedApps() {
  const { t, relativeTime } = useI18n()
  const qc = useQueryClient()
  const info = useQuery({ queryKey: ['mcp'], queryFn: api.mcp.info })
  const [client, setClient] = useState<Client>('claudeCode')
  const [confirming, setConfirming] = useState<string | null>(null)
  const revoke = useMutation({
    mutationFn: api.mcp.revoke,
    onSuccess: () => {
      setConfirming(null)
      toast(t('apps.revoked'))
      void qc.invalidateQueries({ queryKey: ['mcp'] })
    },
    onError: () => toast.error(t('error.generic')),
  })
  const url = info.data?.url ?? ''
  // Linked to from a conversation ("manage connections"): bring this section into view.
  const section = useRef<HTMLElement>(null)
  const ready = !!info.data
  useEffect(() => {
    if (ready && window.location.hash === '#apps') section.current?.scrollIntoView()
  }, [ready])
  const tabs: { value: Client; label: string }[] = [
    { value: 'claudeCode', label: 'Claude Code' },
    { value: 'claude', label: t('apps.claude') },
    { value: 'other', label: t('apps.other') },
  ]
  return (
    <section
      ref={section}
      id="apps"
      className="scroll-mt-20 rounded-lg border border-line bg-surface p-5 shadow-sm"
    >
      <h2 className="text-[15px] font-semibold">{t('apps.title')}</h2>
      <p className="mt-2 text-[13px] leading-relaxed text-muted">{t('apps.body')}</p>
      {info.data && (
        <>
          <div className="mt-4">
            <CopyField value={url} label={t('apps.url')} />
          </div>
          <div
            role="tablist"
            aria-label={t('apps.how')}
            className="mt-4 inline-flex items-center rounded-lg border border-line bg-surface-2 p-0.5"
          >
            {tabs.map((o) => (
              <button
                key={o.value}
                type="button"
                role="tab"
                aria-selected={client === o.value}
                onClick={() => {
                  setClient(o.value)
                }}
                className={clsx(
                  'inline-flex h-7 items-center rounded-md px-2.5 text-[12.5px] font-medium transition-colors',
                  client === o.value
                    ? 'bg-surface text-ink shadow-sm'
                    : 'text-muted hover:text-ink',
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div role="tabpanel" className="mt-3 space-y-2 text-[13px] leading-relaxed">
            {client === 'claudeCode' && (
              <>
                <p>{t('apps.claudeCodeStep')}</p>
                <CopyField
                  value={`claude mcp add --transport http moot ${url}`}
                  label={t('apps.command')}
                />
                <p className="text-muted">{t('apps.approveOnce')}</p>
              </>
            )}
            {client === 'claude' && (
              <>
                <p>{t('apps.claudeStep')}</p>
                <p className="text-muted">{t('apps.approveOnce')}</p>
              </>
            )}
            {client === 'other' && (
              <>
                <p>{t('apps.otherStep')}</p>
                <p className="text-muted">{t('apps.approveOnce')}</p>
              </>
            )}
          </div>

          <h3 className="mt-6 text-[13px] font-semibold">{t('apps.connected')}</h3>
          {info.data.apps.length === 0 ? (
            <p className="mt-1 text-[13px] text-muted">{t('apps.none')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {info.data.apps.map((a) => (
                <li key={a.id} className="flex items-center gap-3 py-2 text-[13px]">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{a.name}</span>
                    <span className="block text-[12px] text-muted">
                      {t('apps.since', { when: relativeTime(a.createdAt) })}
                    </span>
                  </span>
                  {confirming === a.id ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setConfirming(null)
                        }}
                      >
                        {t('apps.keep')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger hover:text-danger"
                        disabled={revoke.isPending}
                        onClick={() => {
                          revoke.mutate(a.id)
                        }}
                      >
                        {t('apps.revokeConfirm')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setConfirming(a.id)
                      }}
                    >
                      {t('apps.revoke')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  )
}
